/**
 * QRCode — generates a QR code image from a URL string.
 *
 * FXC-8723: Used by PlotShareModal to produce a scannable QR for the returned
 * share_url.  Renders to a <canvas> element via the `qrcode` npm package
 * (pure-JS, no native deps) and exposes an imperative download helper.
 *
 * Separate from QRScan (camera-based scanner) — this component GENERATES
 * codes, not reads them.
 */

import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import QRCodeLib from "qrcode";

export interface QRCodeHandle {
  /** Download the QR code as a PNG file. */
  download(filename?: string): void;
  /** Return the canvas data URL (PNG). */
  toDataURL(): string | null;
}

interface QRCodeProps {
  /** The URL/string to encode in the QR code. */
  value: string;
  /** Canvas size in pixels (width = height). Default 256. */
  size?: number;
  /** Error correction level. Default "M". */
  errorCorrectionLevel?: "L" | "M" | "Q" | "H";
  /** Light module colour. Default "#ffffff". */
  colorLight?: string;
  /** Dark module colour. Default "#000000". */
  colorDark?: string;
}

const QRCodeCanvas = forwardRef<QRCodeHandle, QRCodeProps>(function QRCodeCanvas(
  {
    value,
    size = 256,
    errorCorrectionLevel = "M",
    colorLight = "#ffffff",
    colorDark = "#000000",
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !value) return;
    QRCodeLib.toCanvas(canvasRef.current, value, {
      width: size,
      margin: 2,
      errorCorrectionLevel,
      color: { light: colorLight, dark: colorDark },
    }).catch((err: unknown) => {
      console.warn("[QRCode] Failed to render QR code:", err);
    });
  }, [value, size, errorCorrectionLevel, colorLight, colorDark]);

  useImperativeHandle(ref, () => ({
    download(filename = "plot-share-qr.png") {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    },
    toDataURL() {
      return canvasRef.current?.toDataURL("image/png") ?? null;
    },
  }));

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      data-testid="qr-canvas"
      style={{ display: "block", borderRadius: 4 }}
    />
  );
});

export default QRCodeCanvas;
